<script>
import LineChart from '@components/charts/line-chart.vue';
import ClientProgressNutrition from '@components/client/client-progress-nutrition.vue';
import cloneDeep from 'lodash/cloneDeep';
import {
  trainerMethods,
  trainerComputed,
  clientProgramComputed,
  programComputed,
} from '@state/helpers';
import { counterMixin } from '@mixins/counter-mixin';
import resize from '@utils/image-sizer';

export default {
  components: {
    LineChart,
    ClientProgressNutrition,
  },
  mixins: [counterMixin],
  props: {
    goalCharts: {
      type: Object,
      required: true,
      default: () => {},
    },
    client: {
      type: Object,
      required: true,
      default: () => {},
    },
  },
  data() {
    return {
      placeholder: require('@assets/images/placeholder-client-program.jpg'),
      viewProgram: true,
      goalData: {
        datasets: [
          {
            backgroundColor: 'rgba($color-white-wo-rgb, 0)',
            borderColor: 'rgb(64, 89, 225)',
            pointBackgroundColor: 'rgba($color-white-wo-rgb, 0)',
            pointBorderColor: 'rgba($color-white-wo-rgb, 0)',
          },
        ],
      },
      chartOpts: {
        tooltips: {
          enabled: false,
        },
        scales: {
          yAxes: [
            {
              ticks: {
                display: false,
              },
            },
          ],
        },
        labels: {
          display: false,
        },
        legend: {
          display: false,
        },
      },
      noProgram: false,
    };
  },
  computed: {
    ...trainerComputed,
    ...clientProgramComputed,
    ...programComputed,
    weightChartDataArray() {
      return this.getChartDataArray(this.goalCharts.weight.data);
    },
    bmiChartDataArray() {
      return this.getChartDataArray(this.goalCharts.bmi.data);
    },
    waistChartDataArray() {
      return this.getChartDataArray(this.goalCharts.waist.data);
    },
    programcoverPhoto() {
      return this.selectedProgram &&
        this.selectedProgram.mediaAssets &&
        this.selectedProgram.mediaAssets.coverPhoto &&
        this.selectedProgram.mediaAssets.coverPhoto.url
        ? resize(this.selectedProgram.mediaAssets.coverPhoto.url, 1000)
        : this.placeholder;
    },
    exercisesCompleted() {
      return (
        this.clientActivity.client &&
        this.clientActivity.client.metrics &&
        this.clientActivity.client.metrics.exercisesCompleted
      );
    },
    totalWorkoutDuration() {
      return (
        this.clientActivity.client &&
        this.clientActivity.client.metrics &&
        this.clientActivity.client.metrics.totalWorkoutDurationSeconds > 3600 &&
        Math.round(
          this.clientActivity.client.metrics.totalWorkoutDurationSeconds / 3600
        )
      );
    },
    totalWorkouts() {
      return (
        this.clientActivity.client &&
        this.clientActivity.client.metrics &&
        this.clientActivity.client.metrics.workoutsCompleted
      );
    },
  },
  methods: {
    ...trainerMethods,
    getChartDataArray(data) {
      const clonedData = cloneDeep(data);
      clonedData.sort(function(a, b) {
        if (parseInt(a.monthNumber) < parseInt(b.monthNumber)) {
          return -1;
        }
        if (parseInt(a.monthNumber) > parseInt(b.monthNumber)) {
          return 1;
        }
        return 0;
      });
      return clonedData.map((month) => month.current.value);
    },
    calculateBmi(weight, height) {
      if (weight && height) {
        const value = (weight / height / height) * 703;
        return isNaN(value) ? 0 : value.toFixed(2);
      } else {
        return 0;
      }
    },
    toProgram() {
      this.$router.push({ name: 'programs' }).catch(() => {});
    },
    toGoals() {
      this.$router
        .push({
          name: 'clientAbout',
          hash: '#goals',
          params: {
            id: this.clientActivity.client._id,
            client: this.clientActivity.client,
          },
        })
        .catch(() => {});
    },
  },
};
</script>

<template>
  <div class="card-client-progress-container">
    <div class="header">
      <h1>{{ viewProgram ? 'Fitness Program' : 'Nutrition' }}</h1>
      <div class="chevrons-wrapper">
        <div class="icon-chevron-wrapper left" @click="viewProgram = true">
          <img
            class="icon-chevron"
            src="@assets/images/icon-chevron-right.svg"
          />
        </div>
        <div class="icon-chevron-wrapper" @click="viewProgram = false">
          <img
            class="icon-chevron"
            src="@assets/images/icon-chevron-right.svg"
          />
        </div>
      </div>
    </div>
    <div class="cards-container">
      <div
        :class="['card-client-progress', { 'view-program': viewProgram }]"
        transition
      >
        <div class="progress-section">
          <div v-if="noProgram" class="progress-program-wrapper">
            <div class="no-program">
              <h3>No Program Found</h3>
              <p>
                We couldn't find any programs assigned to
                <strong>{{ clientActivity.client.profile.firstName }}</strong
                >. If you need to assign or create a new program, you can go
                ahead and do that.
              </p>
              <BaseButton
                type="button"
                class="btn-primary btn-ghost btn-round"
                @click="toProgram"
              >
                Assign New Program
              </BaseButton>
            </div>
          </div>
          <div v-if="!noProgram" class="progress-program-wrapper">
            <div
              class="progress-program-image-wrapper"
              :style="{
                'background-image': `url(${programcoverPhoto})`,
                'background-position': 'center',
              }"
            >
              <span class="progress-image-tag">Fitness Program</span>
            </div>
            <div class="progress-program-details">
              <div
                v-if="selectedProgram && selectedProgram.label"
                class="progress-program-title"
              >
                <h4>{{ selectedProgram.label }}</h4>
              </div>
              <div class="progress-program-completion">
                <div class="row">
                  <div class="col">
                    <div class="num">
                      {{ totalWorkouts ? totalWorkouts : 0 }}
                    </div>
                    <span>WORKOUTS</span>
                  </div>
                  <span class="divider-vertical">&nbsp;</span>
                  <div class="col">
                    <div class="num">
                      {{ exercisesCompleted ? exercisesCompleted : 0 }}
                    </div>
                    <span>EXERCISES</span>
                  </div>
                  <span class="divider-vertical">&nbsp;</span>
                  <div class="col">
                    <div class="num">
                      {{ totalWorkoutDuration ? totalWorkoutDuration : 0 }}
                    </div>
                    <span>HOURS</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          :class="[
            'progress-section goals',
            {
              empty:
                !client.progress.goals.weight.value &&
                !client.progress.goals.bmi.value &&
                !client.progress.goals.waist.value,
            },
          ]"
        >
          <span class="client-goals-tag">Goals</span>
          <div class="client-goals-wrapper">
            <div class="goal-chart">
              <label>Weight</label>
              <div class="line-chart-wrapper">
                <LineChart
                  v-if="client.progress.goals.weight.value"
                  :data="{
                    ...goalData,
                    datasets: [
                      {
                        ...goalData.datasets[0],
                        data: weightChartDataArray,
                      },
                    ],
                  }"
                  :options="chartOpts"
                /><div v-else>
                  <BaseButton type="button" class="btn-small" @click="toGoals">
                    Add Weight Goal
                  </BaseButton>
                </div>
              </div>
              <h3 class="goal-num">
                {{
                  client.progress.goals.weight.value
                    ? Math.round(client.progress.goals.weight.value)
                    : 0
                }}
                <small>lbs</small>
              </h3>
            </div>
            <div class="goal-chart">
              <label>BMI</label>
              <div class="line-chart-wrapper">
                <LineChart
                  v-if="
                    calculateBmi(
                      client.progress.goals.weight.value,
                      client.progress.initial.height.value
                    )
                  "
                  :data="{
                    ...goalData,
                    datasets: [
                      {
                        ...goalData.datasets[0],
                        data: bmiChartDataArray,
                      },
                    ],
                  }"
                  :options="chartOpts"
                /><div v-else>
                  <BaseButton type="button" class="btn-small" @click="toGoals">
                    Add BMI Goal
                  </BaseButton>
                </div>
              </div>
              <h3 class="goal-num"
                >{{
                  calculateBmi(
                    client.progress.goals.weight.value,
                    client.progress.initial.height.value
                  )
                }}
                <small>kg/m</small></h3
              >
            </div>
            <div class="goal-chart">
              <label>Waist</label>
              <div class="line-chart-wrapper">
                <LineChart
                  v-if="client.progress.goals.waist.value"
                  :data="{
                    ...goalData,
                    datasets: [
                      {
                        ...goalData.datasets[0],
                        data: waistChartDataArray,
                      },
                    ],
                  }"
                  :options="chartOpts"
                /><div v-else>
                  <BaseButton type="button" class="btn-small" @click="toGoals">
                    Add Waist Goal
                  </BaseButton>
                </div>
              </div>
              <h3 class="goal-num">
                {{
                  client.progress.goals.waist.value
                    ? Math.floor(client.progress.goals.waist.value)
                    : 0
                }}
                <small>in</small>
              </h3>
            </div>
          </div>
        </div>
      </div>
      <ClientProgressNutrition
        :viewProgram="viewProgram"
        :thisWeeksMeals="clientActivity.thisWeeksMeals"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@design';
// stylelint-disable no-descending-specificity

.content-wrapper {
  max-width: 920px;
}

.btn-small {
  border-radius: 9999rem;
  display: block;
  margin-left: -15%;
  padding-left: 1rem;
  padding-right: 1rem;
  width: 130%;
}

.card-client-progress-container {
  display: flex;
  flex-direction: column;
  margin-left: 1.6rem;
  margin-right: 1.6rem;
  margin-top: 4rem;
  .header {
    align-items: center;
    display: flex;
    justify-content: space-between;
    h1 {
      transition: all linear 0.3s ease-in-out;
    }
    .chevrons-wrapper {
      display: flex;
      margin-right: 3rem;
    }
  }
  .icon-chevron-wrapper {
    background-color: rgba($color-medium-gray-wo-rgb, 0.3);
    border-radius: 50%;
    bottom: 0;
    display: flex;
    height: 3.2rem;
    justify-content: center;
    overflow: hidden;
    right: 0;
    width: 3.2rem;
    &.left {
      margin-right: 1rem;
      transform: rotateY(180deg);
    }

    &:hover {
      background-color: rgba($color-medium-gray-wo-rgb, 0.8);
      cursor: pointer;
    }
  }
  .cards-container {
    display: flex;
    width: 200%;
  }
}

.card-client-progress {
  // margin-top: 1rem;
  background-color: white;
  border-radius: $base-border-radius-md;
  box-shadow: 0 0.4rem 0.6rem rgba($color-black-wo-rgb, 0.02);
  display: flex;
  margin-bottom: 3rem;
  min-height: 32rem;
  transform: translateX(-100%);
  transition: transform 0.5s ease-in-out;
  width: 50%;

  .progress-section {
    padding: 3rem 2rem;
    // &.goals {
    //   border-top: 1px solid #EAEAEA;
    //   border-bottom: 1px solid #EAEAEA;
    // }

    &:first-child {
      background-color: $color-light-gray;
      border-bottom-left-radius: $base-border-radius-md;
      border-top-left-radius: $base-border-radius-md;
      width: 45%;
    }

    &:last-child {
      width: 55%;
    }
  }

  &.view-program {
    transform: translateX(0%);
    .progress-section {
      &.goals {
        border-bottom: 1px solid $color-light-gray-two;
        border-top: 1px solid $color-light-gray-two;
      }

      &.empty {
        position: relative; /* Sit on top of the page content */
        z-index: 2; /* Specify a stack order in case you're using a different order for other elements */
        .goal-num {
          opacity: 0;
        }
        .line-chart-wrapper {
          text-align: center;
        }
        &::after {
          background-color: rgba($color-photo-overlay-wo-rgb, 0.1);
          border-radius: 8px;
          bottom: 16px;
          color: $color-text;
          content: 'Clients with goals stay longer! Enter Client Goals in Profile';
          font-size: 1.2rem;
          font-weight: bold;
          left: 50%;
          margin: 0 auto;
          margin-left: -25%;
          padding: 1rem;
          position: absolute;
          text-align: center;
          text-transform: uppercase;
        }
      }
    }
  }

  .client-goals-tag,
  .progress-image-tag {
    background-color: rgba($color-black-wo-rgb, 0.7);
    border-radius: $base-border-radius;
    color: white;
    font-size: 1.2rem;
    font-weight: 800;
    letter-spacing: 0.1px;
    line-height: 2.2rem;
    padding: 0.25rem 0.5rem;
    text-transform: uppercase;
  }

  .nutrition-tag {
    background-color: rgba($color-black-wo-rgb, 0.7);
    border-radius: $base-border-radius;
    color: white;
    font-size: 1.2rem;
    font-weight: 800;
    letter-spacing: 0.1px;
    line-height: 2.2rem;
    padding: 0.25rem 0.5rem;
    text-transform: uppercase;
  }

  .client-goals-wrapper {
    display: flex;
    flex-wrap: wrap;
    margin-top: 3rem;

    .goal-chart {
      align-items: center;
      display: flex;
      flex: 1;
      flex-direction: column;
      justify-content: center;

      .line-chart-wrapper {
        height: 8rem;
        width: 8rem;
      }
    }
    .goal-num {
      font-weight: 600;
    }
  }

  .progress-program-wrapper {
    position: relative;
    .no-program {
      align-items: center;
      display: flex;
      flex-direction: column;
      justify-content: center;
      text-align: center;
    }

    .progress-program-image-wrapper {
      background-repeat: no-repeat;
      background-size: cover;
      border-radius: $base-border-radius-sm;
      margin-bottom: 5%;
      margin-left: 1rem;
      margin-right: 1rem;
      // max-width: 340px;
      min-height: 170px;
      padding: 0.7rem;
    }

    .progress-program-details {
      align-items: center;
      display: flex;
      margin-left: 1rem;
      margin-right: 1rem;
      .progress-program-title {
        border-right: 1px solid $color-medium-gray;
        flex: 1;
        padding-right: 1rem;
        text-decoration: underline;
        text-decoration-color: black;

        &:hover {
          cursor: pointer;
        }
      }

      .progress-program-completion {
        color: $color-black;
        display: flex;
        flex: 1;
        font-size: 11px;
        font-style: normal;
        font-weight: 800;
        letter-spacing: 0.0916667px;
        line-height: 22px;
        text-transform: uppercase;

        a {
          text-decoration-color: black;
        }

        .program-completion-percentage {
          color: $color-heading-text;
          font-size: 1.8rem;
          letter-spacing: 0.16429px;
          line-height: 5rem;
          margin: 0 0 0 1.6rem;

          small {
            color: $color-medium-gray-alt;
            display: block;
            font-size: 1.1rem;
            font-weight: 800;
            letter-spacing: 0.0916667px;
            line-height: 0;
            text-transform: uppercase;

            &.percent-symbol {
              color: $color-heading-text;
              display: initial;
            }
          }
        }
        .divider-vertical {
          border-left: 1px solid rgba($color-black-wo-rgb, 0.6);
          height: 2.7rem;
          margin-top: 0.7rem;
          opacity: 0.4;
        }
        .num {
          @include calc-font-size(2.8);
        }
        span {
          @include calc-font-size(0.6);
        }

        .icon-chevron-wrapper {
          background-color: $color-medium-gray;
          border-radius: 50%;
          bottom: 0;
          display: flex;
          height: 3.2rem;
          justify-content: center;
          overflow: hidden;
          position: absolute;
          right: 0;
          width: 3.2rem;

          &:hover {
            cursor: pointer;
          }
        }
      }
    }
  }
}
</style>
